import { X } from 'lucide-react';
import React from 'react';
import { ContextInfo } from '../types';
import './SearchContext.css';

interface SearchContextProps {
    context: ContextInfo;
    onClear: () => void;
    activeSummaryLabel?: string | null;
    onClearSummary?: () => void;
}

const SearchContext: React.FC<SearchContextProps> = ({ context, onClear, activeSummaryLabel, onClearSummary }) => {
    const hasContext = context.aircraft ||
        context.part_name ||
        (context.part_keywords && context.part_keywords.length > 0) ||
        (context.issue_categories && context.issue_categories.length > 0) ||
        (context.systems && context.systems.length > 0) ||
        (context.issue_groups && context.issue_groups.length > 0) ||
        (context.issue_topics && context.issue_topics.length > 0);

    const hasPartAnalysis = !!context.selected_part_identifier;
    const hasSummary = !!activeSummaryLabel;

    if (!hasContext && !hasPartAnalysis && !hasSummary) {
        return null;
    }

    const getSafeText = (value?: string): string => {
        if (!value) return '';
        const parsed = new DOMParser().parseFromString(value, 'text/html');
        return parsed.body.textContent?.trim() || '';
    };

    const getDisplayName = (id: string, type: 'group' | 'topic'): string => {
        if (type === 'group' && context.group_display_names?.[id]) {
            return context.group_display_names[id];
        }
        if (type === 'topic' && context.topic_display_names?.[id]) {
            return context.topic_display_names[id];
        }

        if (import.meta.env.MODE === 'development') {
            console.warn(`Missing ${type} display name for: ${id}`);
        }

        return id.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
        <div className="context-bars">
            {hasContext && (
                <div className="context-bar discovery-bar">
                    <div className="context-bar-content">
                        {context.aircraft && (
                            <span className="context-item">
                                <strong>Aircraft:</strong> {context.aircraft}
                            </span>
                        )}
                        {context.part_keywords && context.part_keywords.length > 0 && !context.selected_part_identifier ? (
                            <span className="context-item">
                                <strong>Keywords:</strong> {context.part_keywords.join(' + ')}
                            </span>
                        ) : context.part_name && (
                            <span className="context-item">
                                <strong>Part:</strong> <span>{getSafeText(context.part_name)}</span>
                            </span>
                        )}
                        {context.issue_categories && context.issue_categories.length > 0 && (
                            <span className="context-item">
                                <strong>Category:</strong> {context.issue_categories.join(', ')}
                            </span>
                        )}
                        {context.systems && context.systems.length > 0 && (
                            <span className="context-item">
                                <strong>Systems:</strong> {context.systems.join(', ')}
                            </span>
                        )}
                        {context.issue_groups && context.issue_groups.length > 0 && (
                            <span className="context-item">
                                <strong>Group:</strong> {context.issue_groups.map(g => getDisplayName(g, 'group')).join(', ')}
                            </span>
                        )}
                        {context.issue_topics && context.issue_topics.length > 0 && (
                            <span className="context-item">
                                <strong>Topic:</strong> {context.issue_topics.map(t => getDisplayName(t, 'topic')).join(', ')}
                            </span>
                        )}
                    </div>
                    <button
                        className="context-bar-close"
                        onClick={onClear}
                        aria-label="Clear search context"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
            {hasPartAnalysis && (
                <div className="context-bar analysis-bar">
                    <div className="context-bar-content">
                        <span className="context-bar-label">Findings Summary for the Part</span>
                    </div>
                </div>
            )}
            {hasSummary && (
                <div className="context-bar summary-bar">
                    <div className="context-bar-content">
                        <span className="context-bar-label">{activeSummaryLabel}</span>
                    </div>
                    <button
                        className="context-bar-close"
                        onClick={onClearSummary}
                        aria-label="Clear summary"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
        </div>
    );
};

export { SearchContext };
